import React from "react";
import { Wallet, ArrowRight, Receipt } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { formatCurrency } from '../../utils/currencyFormatter';

const SummaryCard = ({ totalIncome, totalExpense, selectedMonth, transactions }) => {
  const navigate = useNavigate();

  const handleNavigate = (type) => {
    navigate('/transactions', { state: { initialFilter: type } });
  };

  // Calculate previous month's totals
  const getPreviousMonthData = () => {
    const previousMonth = selectedMonth === 0 ? 11 : selectedMonth - 1;
    const previousMonthTransactions = transactions.filter(t => {
      const transactionDate = new Date(t.date);
      return transactionDate.getMonth() === previousMonth;
    });

    const prevIncome = previousMonthTransactions
      .filter(t => t.type === "income")
      .reduce((sum, t) => sum + t.amount, 0);

    const prevExpense = previousMonthTransactions
      .filter(t => t.type === "expense")
      .reduce((sum, t) => sum + t.amount, 0);

    return { prevIncome, prevExpense };
  };

  const { prevIncome, prevExpense } = getPreviousMonthData();

  // Calculate differences
  const incomeDiff = totalIncome - prevIncome;
  const expenseDiff = totalExpense - prevExpense;

  // Get month names
  const currentMonthName = new Date(2024, selectedMonth).toLocaleString('default', { month: 'long' });
  const prevMonthName = new Date(2024, selectedMonth === 0 ? 11 : selectedMonth - 1).toLocaleString('default', { month: 'long' });

  // Format comparison text
  const getComparisonText = (diff, type) => {
    if (diff === 0) return `Same as ${prevMonthName}`;
    const absValue = Math.abs(diff);
    return `${formatCurrency(absValue)} ${diff > 0 ? 'more' : 'less'} than ${prevMonthName}`;
  };

  return (
    <div className="m-2 shadow p-2 bg-light" style={{
      position: "relative",
      bottom: "80px",
    }}>
      {/* Income Card */}
      <div 
        className="d-flex align-items-center justify-content-between mb-3"
        onClick={() => handleNavigate('income')}
        style={{ cursor: 'pointer' }}
        role="button"
      >
        <div className="d-flex align-items-center gap-3">
          <div className="p-2 bg-success-subtle rounded-circle">
            <Wallet className="text-success" style={{ width: "20px", height: "20px" }} />
          </div>
          <div>
            <p className="mb-0 fw-medium">Income</p>
            <p className="small text-muted mb-0">
              {getComparisonText(incomeDiff, 'income')}
            </p>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <span className="fw-semibold">{formatCurrency(totalIncome)}</span>
          <ArrowRight 
            className="text-success" 
            style={{ width: "16px", height: "16px" }} 
          />
        </div>
      </div>

      {/* Expense Card */}
      <div 
        className="d-flex align-items-center justify-content-between"
        onClick={() => handleNavigate('expense')}
        style={{ cursor: 'pointer' }}
        role="button"
      >
        <div className="d-flex align-items-center gap-3">
          <div className="p-2 bg-success-subtle rounded-circle">
            <Receipt className="text-success" style={{ width: "20px", height: "20px" }} />
          </div>
          <div>
            <p className="mb-0 fw-medium">Expense</p>
            <p className="small text-muted mb-0">
              {getComparisonText(expenseDiff, 'expense')}
            </p>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <span className="fw-semibold">{formatCurrency(totalExpense)}</span>
          <ArrowRight 
            className="text-success" 
            style={{ width: "16px", height: "16px" }} 
          />
        </div>
      </div>
    </div>
  );
};

export default SummaryCard;
